type InsightDisplayName = {
  description?: string;
  name?: string;
  riskType?: string;
  sensitivityStr?: string;
  shareTypeStr?: string;
  type?: string;
};

const PLACEHOLDER_VALUES = new Set(["", "None", "General content"]);

const SENSITIVITY_NAME_PATTERNS: Array<{
  pattern: RegExp;
  displayName: string;
}> = [
  { pattern: /^Non[- ]sensitive\b/i, displayName: "General content" },
  { pattern: /^Low\+\s+sensitivity\b/i, displayName: "Low sensitivity content" },
  { pattern: /^Low\s+sensitivity\b/i, displayName: "Low sensitivity content" },
  {
    pattern: /^Medium\+\s+sensitivity\b/i,
    displayName: "Medium sensitivity content",
  },
  {
    pattern: /^Medium\s+sensitivity\b/i,
    displayName: "Medium sensitivity content",
  },
  {
    pattern: /^Highly\s+sensitive\b/i,
    displayName: "Highly sensitive content",
  },
  {
    pattern: /^High\s+sensitivity\b/i,
    displayName: "Highly sensitive content",
  },
  { pattern: /^Critical\b/i, displayName: "Highly sensitive content" },
];

const SHARE_TYPE_NAME_PATTERNS: Array<{
  pattern: RegExp;
  displayName: string;
}> = [
  {
    pattern: /^Shared inside(?: the)? organization$/i,
    displayName: "Shared inside the organization",
  },
  {
    pattern: /^Shared outside(?: of| the)? organization$/i,
    displayName: "Shared outside the organization",
  },
  {
    pattern: /^Shared to personal address$/i,
    displayName: "Shared to a private email address",
  },
  {
    pattern: /^Shared via unsecure link$/i,
    displayName: "Shared via anonymous link",
  },
  {
    pattern: /^Shared via anonymous link$/i,
    displayName: "Shared via anonymous link",
  },
  {
    pattern: /^Shared to untrusted employee$/i,
    displayName: "Shared to untrusted employee",
  },
  {
    pattern: /^Shared to untrusted organization$/i,
    displayName: "Shared to untrusted organization",
  },
  {
    pattern: /^Shared to untrusted personal address$/i,
    displayName: "Shared to untrusted personal address",
  },
  {
    pattern: /^Shared to untrusted folder$/i,
    displayName: "Shared to untrusted folder",
  },
  {
    pattern: /^Insecurely shared by untrusted user$/i,
    displayName: "Insecurely shared by untrusted user",
  },
];

const getMeaningfulValue = (value?: string) =>
  value && !PLACEHOLDER_VALUES.has(value) ? value : "";

const getNormalizedInsightDisplayName = (insight: InsightDisplayName) => {
  const sensitivity = getMeaningfulValue(insight.sensitivityStr);
  const shareType = getMeaningfulValue(insight.shareTypeStr);
  const subject =
    insight.riskType === "internalInformationExposed" && sensitivity
      ? `Internal ${sensitivity}`
      : sensitivity;

  return [subject, shareType].filter(Boolean).join(", ");
};

const getSensitivityDisplayName = (value: string) =>
  SENSITIVITY_NAME_PATTERNS.find(({ pattern }) => pattern.test(value))
    ?.displayName || "";

const getShareTypeDisplayName = (value: string) => {
  const withoutQualifiers = value.replace(/,\s*Anomalously.*$/i, "").trim();
  return (
    SHARE_TYPE_NAME_PATTERNS.find(({ pattern }) =>
      pattern.test(withoutQualifiers)
    )?.displayName || ""
  );
};

const getRawInsightDisplayName = (value: string) => {
  const match = value
    .trim()
    .match(/^(.*?)(?:,\s*|\s+)((?:Shared|Insecurely shared)\b.*)$/i);

  if (!match) return "";

  const sensitivity = getSensitivityDisplayName(match[1]);
  const shareType = getShareTypeDisplayName(match[2]);
  return sensitivity && shareType ? `${sensitivity}, ${shareType}` : "";
};

export const getInsightDisplayName = (insight?: InsightDisplayName | null) => {
  if (!insight) return "";

  const description = insight.description || "";
  const name = insight.name || "";
  const normalizedDisplayName =
    insight.type === "Compliance"
      ? getNormalizedInsightDisplayName(insight)
      : "";

  if (normalizedDisplayName) return normalizedDisplayName;

  const displayName = description || name;
  const rawDisplayName = getRawInsightDisplayName(displayName);

  if (rawDisplayName) return rawDisplayName;
  if (description) return description;

  return name;
};
